const PAPER_STORAGE_KEY = 'momo-paper-trading-v1';
const PAPER_CASH_STORAGE_KEY = 'momo-paper-cash-v1';
export const PAPER_MODE_EVENT = 'momo-paper-mode-changed';
/** Fired when the confirmed paper cash balance changes (set or reset). */
export const PAPER_CASH_CHANGED_EVENT = 'momo-paper-cash-changed';
/** Matches server default in netlify/functions/_lib/paper-trading.ts */
export const DEFAULT_PAPER_CASH = 100_000;

export interface PaperCashChangedDetail {
  cash: number;
  /** True when the whole paper account (cash + positions) was reset, not just cash. */
  reset: boolean;
}

export function isPaperTradingEnabled(): boolean {
  try {
    return localStorage.getItem(PAPER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setPaperTradingEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(PAPER_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Ignore quota / private-mode errors.
  }
  window.dispatchEvent(new CustomEvent(PAPER_MODE_EVENT, { detail: { enabled } }));
}

/** Last confirmed paper starting/cash balance for the balance form. */
export function getStoredPaperCash(): number {
  try {
    const raw = localStorage.getItem(PAPER_CASH_STORAGE_KEY);
    if (raw == null) return DEFAULT_PAPER_CASH;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return DEFAULT_PAPER_CASH;
    return value;
  } catch {
    return DEFAULT_PAPER_CASH;
  }
}

/**
 * Persist the confirmed paper cash balance. Dispatches `PAPER_CASH_CHANGED_EVENT`
 * whenever the value actually changes (or on an explicit reset) so any component
 * holding a stale portfolio snapshot (e.g. the positions panel that drives the
 * header's book-value badge) knows to refresh.
 */
export function setStoredPaperCash(cash: number, options?: { reset?: boolean }): void {
  if (!Number.isFinite(cash) || cash < 0) return;
  const rounded = Math.round(cash);
  const previous = getStoredPaperCash();
  try {
    localStorage.setItem(PAPER_CASH_STORAGE_KEY, String(rounded));
  } catch {
    // Ignore quota / private-mode errors.
  }
  const reset = options?.reset ?? false;
  if (rounded !== previous || reset) {
    window.dispatchEvent(
      new CustomEvent<PaperCashChangedDetail>(PAPER_CASH_CHANGED_EVENT, {
        detail: { cash: rounded, reset },
      }),
    );
  }
}

export function paperTradingHeaders(): HeadersInit {
  return isPaperTradingEnabled() ? { 'X-Momo-Paper': '1' } : {};
}

export function paperTradingQuerySuffix(): string {
  return isPaperTradingEnabled() ? '&paper=1' : '';
}

export function formatPaperMoney(value: number): string {
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}
